/**
 * RecorderEngine — captura da voz.
 *
 * Caminho principal: AudioWorklet (PCM Float32 cru + timestamp por bloco no
 * relógio do AudioContext). Isso dá precisão de sample para sincronização,
 * pause/resume sem perder a base de tempo e punch-in/out real.
 *
 * Caminho alternativo (fallback): MediaRecorder + decodeAudioData, usado quando
 * AudioWorklet não está disponível. Funciona, mas com precisão menor — e a UI
 * mostra isso com honestidade.
 *
 * Nunca usamos o "relógio de parede" como única fonte: guardamos
 * ctx.currentTime e performance.now() de cada evento (início, pause, resume,
 * seek) para que a SyncEngine possa reconstruir a linha do tempo.
 */
import type { MicDeviceInfo, RecordedSegment } from '../types.js';
import { clamp } from '../utils/misc.js';
import { concatMono } from './buffers.js';

export interface RecorderStartOptions {
  /** Posição na linha do tempo da música onde a gravação começa (s). */
  position?: number;
  /** ctx.currentTime exato do primeiro sample (agendamento preciso). */
  startCtxTime?: number;
  /** performance.now() do play/seek do player de referência. */
  mediaRequestedAtPerf?: number;
  /** Descarta o silêncio inicial (útil para punch-in). */
  dropLeadingSilence?: boolean;
  deviceId?: string;
  label?: string;
}

export function recordingGainFromBoostPercent(percent: number): number {
  const safePercent = clamp(Number.isFinite(percent) ? percent : 0, 0, 100);
  return 1 + safePercent / 100;
}

export interface RecorderStopResult {
  segments: RecordedSegment[];
  durationSec: number;
  peak: number;
  clipped: boolean;
  method: 'audioworklet' | 'mediarecorder';
  diag: {
    micLatencyMs: number;
    deviceLabel: string;
    echoCancellation: boolean;
    noiseSuppression: boolean;
    autoGainControl: boolean;
    sampleRate: number;
    fallbackReason?: string;
  };
  pauseEvents: number[];
  resumeEvents: number[];
}

export type RecorderStatus =
  | 'idle'
  | 'requesting'
  | 'ready'
  | 'recording'
  | 'paused'
  | 'stopping'
  | 'error'
  | 'unsupported';

export interface RecorderEvents {
  onLevel?: (level: { peak: number; rms: number }) => void;
  onStateChange?: (state: RecorderStatus) => void;
  onError?: (message: string, raw?: unknown) => void;
  onNoInput?: () => void;
  onWaitingSignal?: () => void;
}

const CONSTRAINT_LADDER: MediaStreamConstraints[] = [
  // Preferido: captação "crua", sem o navegador alterar o que cantamos.
  { audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false } },
  {
    audio: {
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
      channelCount: 1,
    },
  },
  { audio: true },
];

export class RecorderEngine {
  status: RecorderStatus = 'idle';
  stream: MediaStream | null = null;
  devices: MicDeviceInfo[] = [];
  method: 'audioworklet' | 'mediarecorder' = 'audioworklet';
  level: { peak: number; rms: number } = { peak: 0, rms: 0 };
  lastError: string | null = null;
  lastDiag: RecorderStopResult['diag'] | null = null;
  fallbackReason?: string;

  private events: RecorderEvents = {};
  private ctx: AudioContext | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private analyser: AnalyserNode | null = null;
  private captureGain: GainNode | null = null;
  private captureSink: GainNode | null = null;
  private recordDestination: MediaStreamAudioDestinationNode | null = null;
  private inputGain = 1;
  private mediaRecorder: MediaRecorder | null = null;
  private mrChunks: Blob[] = [];
  private mrStartedAtPerf = 0;
  private blocks: { time: number; perf: number; samples: Float32Array; dropped: number; segmentIndex: number }[] = [];
  private segmentIndex = 0;
  private segmentPositions = new Map<number, number>();
  private segStartCtx = 0;
  private segStartPerf = 0;
  private segPosition = 0;
  private segMediaRequestedAtPerf = 0;
  private startedAtCtx = 0;
  private startedAtPerf = 0;
  private startPosition = 0;
  private mediaRequestedAtPerf = 0;
  private pauseEvents: number[] = [];
  private resumeEvents: number[] = [];
  private meterTimer: number | null = null;
  private workletsReady = false;
  private pendingStopResolve: ((r: RecorderStopResult) => void) | null = null;
  private stopTimeout: number | null = null;
  private accumulated = 0;

  on(events: RecorderEvents) {
    this.events = { ...this.events, ...events };
  }

  /** Reforço digital antes da gravação: 0% mantém sinal original, 100% dobra a amplitude. */
  setRecordingGainBoostPercent(percent: number) {
    this.inputGain = recordingGainFromBoostPercent(percent);
    if (this.captureGain && this.ctx) {
      this.captureGain.gain.setTargetAtTime(this.inputGain, this.ctx.currentTime, 0.015);
    }
  }

  private setState(s: RecorderStatus) {
    this.status = s;
    this.events.onStateChange?.(s);
  }

  private get ctxTime(): number {
    return this.ctx?.currentTime ?? performance.now() / 1000;
  }

  // ------------------------------------------------------------------
  // Dispositivos e permissão
  // ------------------------------------------------------------------

  static classifyDevice(label: string): MicDeviceInfo['kind'] {
    const l = (label || '').toLowerCase();
    if (!l) return 'other';
    if (/bluetooth|airpods|buds|beats|wh-|wf-|jbl|galaxy buds|headset bt|\bbs\b/.test(l)) return 'bluetooth';
    if (/usb|interface|focusrite|scarlett|motu|apollo|steinberg|behringer|umc|komplete|zoom/.test(l)) return 'usb';
    if (/headset|earbud|fones|headphone|in-ear|airpods/.test(l)) return 'headset';
    if (/default|padr/.test(l)) return 'default';
    return 'other';
  }

  async listDevices(): Promise<MicDeviceInfo[]> {
    if (!navigator.mediaDevices?.enumerateDevices) return [];
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      this.devices = all
        .filter((d) => d.kind === 'audioinput')
        .map((d, i) => ({
          deviceId: d.deviceId,
          label: d.label || `Microfone ${i + 1}`,
          kind: RecorderEngine.classifyDevice(d.label),
        }));
      return this.devices;
    } catch {
      return [];
    }
  }

  /**
   * Pede acesso ao microfone com fallback de constraints.
   * Mensagens de erro sempre em português claro (nunca "NotAllowedError").
   */
  async requestPermission(deviceId?: string, ctx?: AudioContext | null): Promise<MediaStream> {
    this.ctx = ctx ?? this.ctx;
    this.setState('requesting');
    if (!navigator.mediaDevices?.getUserMedia) {
      this.setState('unsupported');
      const msg =
        'Este navegador não permite acessar o microfone. Use Chrome, Edge, Firefox ou Safari em HTTPS.';
      this.lastError = msg;
      this.events.onError?.(msg);
      throw new Error(msg);
    }

    let lastErr: unknown = null;
    for (const base of CONSTRAINT_LADDER) {
      const audio =
        typeof base.audio === 'object' && base.audio
          ? { ...(base.audio as MediaTrackConstraints), ...(deviceId ? { deviceId: { exact: deviceId } } : {}) }
          : deviceId
          ? { deviceId: { exact: deviceId } }
          : true;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio });
        this.stream = stream;
        this.fallbackReason = undefined;
        this.setState('ready');
        await this.listDevices();
        return stream;
      } catch (err) {
        lastErr = err;
        // deviceId exato não existe? tenta sem ele.
        if (deviceId) {
          try {
            const stream = await navigator.mediaDevices.getUserMedia(base);
            this.stream = stream;
            this.fallbackReason = 'O microfone escolhido não está mais disponível; usamos o padrão.';
            this.setState('ready');
            return stream;
          } catch (err2) {
            lastErr = err2;
          }
        }
      }
    }
    const msg = describeMicError(lastErr);
    this.lastError = msg;
    this.setState('error');
    this.events.onError?.(msg, lastErr);
    throw new Error(msg);
  }

  /** Troca de microfone sem perder o contexto de áudio. */
  async switchDevice(deviceId: string): Promise<void> {
    this.stopMeterLoop();
    this.clearCaptureGraph();
    this.releaseStream();
    await this.requestPermission(deviceId, this.ctx);
  }

  private clearCaptureGraph() {
    try { this.workletNode?.port.close(); } catch { /* noop */ }
    try { this.workletNode?.disconnect(); } catch { /* noop */ }
    try { this.sourceNode?.disconnect(); } catch { /* noop */ }
    try { this.captureGain?.disconnect(); } catch { /* noop */ }
    try { this.analyser?.disconnect(); } catch { /* noop */ }
    try { this.captureSink?.disconnect(); } catch { /* noop */ }
    try { this.recordDestination?.disconnect(); } catch { /* noop */ }
    this.recordDestination?.stream.getTracks().forEach((track) => track.stop());
    this.workletNode = null;
    this.sourceNode = null;
    this.captureGain = null;
    this.analyser = null;
    this.captureSink = null;
    this.recordDestination = null;
  }

  private releaseStream() {
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
  }

  private trackSettings(): MediaTrackSettings {
    const t = this.stream?.getAudioTracks?.()[0];
    return (t?.getSettings?.() as MediaTrackSettings) ?? {};
  }

  // ------------------------------------------------------------------
  // Preparação do caminho de captura
  // ------------------------------------------------------------------

  private async prepareCapture(ctx: AudioContext): Promise<boolean> {
    this.ctx = ctx;
    if (!this.stream) throw new Error('Microfone não autorizado.');
    if (ctx.state === 'suspended') await ctx.resume().catch(() => {});
    // Troca um monitor anterior pelo caminho de gravação, sem deixar nós vivos.
    this.clearCaptureGraph();
    this.sourceNode = ctx.createMediaStreamSource(this.stream);
    this.captureGain = ctx.createGain();
    this.captureGain.gain.value = this.inputGain;
    this.sourceNode.connect(this.captureGain);

    // Medidor pós-ganho: mostra exatamente o nível que será gravado.
    this.analyser = ctx.createAnalyser();
    this.analyser.fftSize = 1024;
    this.analyser.smoothingTimeConstant = 0;
    this.captureGain.connect(this.analyser);
    this.captureSink = ctx.createGain();
    this.captureSink.gain.value = 0; // mantém o grafo de áudio ativo sem monitorar a voz
    this.sourceNode.connect(this.captureSink);
    this.captureSink.connect(ctx.destination);

    if (!ctx.audioWorklet) {
      this.workletsReady = false;
      this.prepareMediaRecorderInput(ctx);
      return false;
    }
    if (!this.workletsReady) {
      try {
        await ctx.audioWorklet.addModule(
          new URL('../workers/capture-processor.js', import.meta.url).href
        );
        this.workletsReady = true;
      } catch (err) {
        console.warn('[RecorderEngine] AudioWorklet indisponível, usando MediaRecorder:', err);
        this.workletsReady = false;
      }
    }
    if (!this.workletsReady) {
      this.prepareMediaRecorderInput(ctx);
      return false;
    }

    this.workletNode = new AudioWorkletNode(ctx, 'vocal-capture', {
      numberOfInputs: 1,
      numberOfOutputs: 0,
      channelCount: 1,
    });
    this.captureGain.connect(this.workletNode);
    this.workletNode.port.onmessage = (e: MessageEvent) => this.onWorkletMessage(e.data);
    return true;
  }

  private prepareMediaRecorderInput(ctx: AudioContext) {
    this.recordDestination = ctx.createMediaStreamDestination();
    this.captureGain?.connect(this.recordDestination);
  }

  private onWorkletMessage(m: any) {
    if (!m) return;
    if (m.type === 'level') {
      this.level = { peak: m.peak ?? 0, rms: m.rms ?? 0 };
      this.events.onLevel?.(this.level);
    } else if (m.type === 'block') {
      const samples: Float32Array = m.samples ?? new Float32Array(0);
      if (samples.length) {
        this.blocks.push({
          time: typeof m.time === 'number' ? m.time : this.segStartCtx,
          perf: m.perf || this.segStartPerf,
          samples,
          dropped: m.droppedSamples || 0,
          segmentIndex: m.segmentIndex ?? 0,
        });
        this.accumulated += samples.length;
      }
      if (m.final && this.pendingStopResolve) {
        this.finishStop();
      }
    } else if (m.type === 'no-input') {
      this.events.onNoInput?.();
    } else if (m.type === 'waiting-signal') {
      this.events.onWaitingSignal?.();
    }
  }

  private startMeterLoop() {
    this.stopMeterLoop();
    const buf = new Float32Array(this.analyser?.fftSize ?? 1024);
    const tick = () => {
      this.meterTimer = requestAnimationFrame(tick);
      if (!this.analyser) return;
      this.analyser.getFloatTimeDomainData(buf);
      let p = 0, sum = 0;
      for (let i = 0; i < buf.length; i++) {
        const a = Math.abs(buf[i]);
        if (a > p) p = a;
        sum += buf[i] * buf[i];
      }
      this.level = { peak: p, rms: Math.sqrt(sum / buf.length) };
      this.events.onLevel?.(this.level);
    };
    this.meterTimer = requestAnimationFrame(tick);
  }

  private stopMeterLoop() {
    if (this.meterTimer != null) {
      cancelAnimationFrame(this.meterTimer);
      this.meterTimer = null;
    }
  }

  // ------------------------------------------------------------------
  // Ciclo de gravação
  // ------------------------------------------------------------------

  /**
   * Inicia a captura. Se `startCtxTime` for informado, o primeiro sample é
   * associado exatamente a esse instante (usado com contagem/agendamento).
   */
  async start(opts: RecorderStartOptions = {}): Promise<void> {
    if (!this.ctx) throw new Error('AudioContext não inicializado.');
    if (!this.stream) await this.requestPermission(opts.deviceId, this.ctx);
    const useWorklet = await this.prepareCapture(this.ctx!);
    this.method = useWorklet ? 'audioworklet' : 'mediarecorder';
    if (!useWorklet) {
      this.fallbackReason =
        'AudioWorklet indisponível neste navegador: gravando via MediaRecorder (precisão de sincronização menor).';
    }

    this.blocks = [];
    this.mrChunks = [];
    this.segmentIndex = 0;
    this.segmentPositions.clear();
    this.segmentPositions.set(0, this.startPosition);
    this.pauseEvents = [];
    this.resumeEvents = [];
    this.accumulated = 0;
    this.startPosition = opts.position ?? 0;
    this.mediaRequestedAtPerf = opts.mediaRequestedAtPerf ?? performance.now();

    const when = opts.startCtxTime ?? this.ctx!.currentTime + 0.02;
    this.segPosition = this.startPosition;
    this.segStartCtx = when;
    this.segStartPerf = performance.now();
    this.startedAtCtx = when;
    this.startedAtPerf = this.segStartPerf;

    if (useWorklet && this.workletNode) {
      this.workletNode.port.postMessage({
        type: 'start',
        startCtxTime: when,
        startPerf: this.segStartPerf,
        segmentIndex: 0,
        dropLeadingSilence: !!opts.dropLeadingSilence,
        blockSize: 8192,
      });
    } else {
      this.startMediaRecorder();
    }
    this.startMeterLoop();
    this.setState('recording');
  }

  private startMediaRecorder() {
    if (!this.stream) return;
    const recordingStream = this.recordDestination?.stream ?? this.stream;
    const mime = pickRecorderMime();
    try {
      this.mediaRecorder = mime ? new MediaRecorder(recordingStream, { mimeType: mime }) : new MediaRecorder(recordingStream);
    } catch {
      this.mediaRecorder = new MediaRecorder(recordingStream);
    }
    this.mrChunks = [];
    this.mediaRecorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) this.mrChunks.push(e.data);
    };
    this.mediaRecorder.onstop = () => {
      if (this.pendingStopResolve) this.finishStop();
    };
    this.mrStartedAtPerf = performance.now();
    this.mediaRecorder.start(250);
  }

  pause(): void {
    if (this.status !== 'recording') return;
    const ctxTime = this.ctxTime;
    const perf = performance.now();
    this.pauseEvents.push(ctxTime);
    if (this.workletNode) this.workletNode.port.postMessage({ type: 'pause' });
    else if (this.mediaRecorder && this.mediaRecorder.state === 'recording') this.mediaRecorder.pause();
    this.setState('paused');
    void perf;
  }

  resume(opts: { position?: number } = {}): void {
    if (this.status !== 'paused') return;
    const ctxTime = this.ctxTime;
    const perf = performance.now();
    this.resumeEvents.push(ctxTime);
    this.segmentIndex++;
    // Novo segmento: a posição na música continua de onde a voz parou.
    const recordedSoFar = this.accumulated / (this.ctx?.sampleRate ?? 48000);
    this.segPosition = opts.position ?? this.startPosition + recordedSoFar;
    this.segmentPositions.set(this.segmentIndex, this.segPosition);
    this.segStartCtx = ctxTime;
    this.segStartPerf = perf;
    if (this.workletNode) {
      this.workletNode.port.postMessage({
        type: 'resume',
        startCtxTime: ctxTime,
        startPerf: perf,
        segmentIndex: this.segmentIndex,
      });
    } else if (this.mediaRecorder && this.mediaRecorder.state === 'paused') {
      this.mediaRecorder.resume();
    }
    this.setState('recording');
  }

  /**
   * Para e devolve os segmentos gravados, com todos os timestamps.
   */
  async stop(): Promise<RecorderStopResult> {
    if (this.status !== 'recording' && this.status !== 'paused') {
      return this.emptyResult();
    }
    this.setState('stopping');
    return new Promise<RecorderStopResult>((resolve) => {
      this.pendingStopResolve = resolve;
      this.stopTimeout = window.setTimeout(() => {
        // Segurança: nunca deixar o usuário preso em "parando".
        if (this.pendingStopResolve) this.finishStop();
      }, 2500);
      if (this.workletNode) {
        this.workletNode.port.postMessage({ type: 'stop' });
      }
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        try {
          this.mediaRecorder.stop();
        } catch {
          /* noop */
        }
      }
      if (!this.workletNode && (!this.mediaRecorder || this.mediaRecorder.state === 'inactive')) {
        this.finishStop();
      }
    });
  }

  private emptyResult(): RecorderStopResult {
    return {
      segments: [],
      durationSec: 0,
      peak: 0,
      clipped: false,
      method: this.method,
      diag: this.buildDiag(),
      pauseEvents: [],
      resumeEvents: [],
    };
  }

  private buildDiag(): RecorderStopResult['diag'] {
    const s = this.trackSettings() as MediaTrackSettings & { latency?: number };
    const audioTrack = this.stream?.getAudioTracks?.()[0];
    return {
      micLatencyMs: (typeof s.latency === 'number' ? s.latency : 0) * 1000,
      deviceLabel: audioTrack?.label || 'desconhecido',
      echoCancellation: !!s.echoCancellation,
      noiseSuppression: !!s.noiseSuppression,
      autoGainControl: !!s.autoGainControl,
      sampleRate: this.ctx?.sampleRate ?? s.sampleRate ?? 0,
      fallbackReason: this.fallbackReason,
    };
  }

  private async finishStop() {
    if (this.stopTimeout != null) {
      clearTimeout(this.stopTimeout);
      this.stopTimeout = null;
    }
    this.stopMeterLoop();
    const sr = this.ctx?.sampleRate ?? 48000;
    let segments: RecordedSegment[] = [];

    if (this.method === 'audioworklet') {
      segments = this.buildSegmentsFromBlocks(sr);
    } else {
      segments = await this.buildSegmentsFromMediaRecorder(sr);
    }

    let peak = 0;
    let clipped = false;
    let duration = 0;
    for (const s of segments) {
      peak = Math.max(peak, s.peak);
      clipped = clipped || s.clipped;
      duration += s.samples.length / s.sampleRate;
    }

    const result: RecorderStopResult = {
      segments,
      durationSec: duration,
      peak,
      clipped,
      method: this.method,
      diag: this.buildDiag(),
      pauseEvents: [...this.pauseEvents],
      resumeEvents: [...this.resumeEvents],
    };
    this.lastDiag = result.diag;
    this.teardownCapture();
    this.setState('idle');
    const resolve = this.pendingStopResolve;
    this.pendingStopResolve = null;
    resolve?.(result);
  }

  private buildSegmentsFromBlocks(sr: number): RecordedSegment[] {
    if (!this.blocks.length) return [];
    // AudioWorklet inclui segmentIndex em cada bloco. Pause/resume vira uma
    // nova região com posição explícita na timeline, sem contar o tempo parado.
    const byIndex = new Map<number, typeof this.blocks>();
    for (const b of this.blocks) {
      const list = byIndex.get(b.segmentIndex) ?? [];
      list.push(b);
      byIndex.set(b.segmentIndex, list);
    }
    const groups = [...byIndex.entries()].sort((a, b) => a[0] - b[0]);
    const out: RecordedSegment[] = [];
    for (const [segmentIndex, group] of groups) {
      if (!group.length) continue;
      const g = [...group].sort((a, b) => a.time - b.time);
      const data = concatMonoBuffers(g.map((b) => b.samples));
      const first = g[0];
      const dropped = first.dropped / sr;
      const startCtx = first.time + dropped;
      const basePosition = this.segmentPositions.get(segmentIndex) ?? this.startPosition;
      const position = basePosition + dropped;
      const p = peakOf(data);
      const trackSettings = this.trackSettings() as MediaTrackSettings & { latency?: number };
      const trackLatency = typeof trackSettings.latency === 'number' ? trackSettings.latency : 0;
      out.push({
        id: `seg_${Date.now().toString(36)}_${segmentIndex}`,
        position: Math.max(0, position),
        samples: data,
        sampleRate: sr,
        startedAtCtx: startCtx,
        startedAtPerf: first.perf || this.startedAtPerf,
        mediaRequestedAtPerf: this.mediaRequestedAtPerf,
        inputLatencySec: trackLatency,
        peak: p,
        clipped: p >= 0.999,
        deviceId: trackSettings.deviceId,
        label: `Trecho ${segmentIndex + 1}`,
      });
    }
    return out;
  }

  private async buildSegmentsFromMediaRecorder(sr: number): Promise<RecordedSegment[]> {
    if (!this.mrChunks.length || !this.ctx) return [];
    const blob = new Blob(this.mrChunks, { type: this.mediaRecorder?.mimeType || 'audio/webm' });
    let buffer: AudioBuffer;
    try {
      buffer = await this.ctx.decodeAudioData(await blob.arrayBuffer());
    } catch (err) {
      console.warn('[RecorderEngine] decodeAudioData falhou:', err);
      this.events.onError?.('Não foi possível decodificar a gravação.', err);
      return [];
    }
    const data = buffer.numberOfChannels > 1 ? downmix(buffer) : Float32Array.from(buffer.getChannelData(0));
    const startCtx = this.startedAtCtx + (this.mrStartedAtPerf - this.startedAtPerf) / 1000;
    return [
      {
        id: `seg_${Date.now().toString(36)}_0`,
        position: this.startPosition,
        samples: data,
        sampleRate: buffer.sampleRate,
        startedAtCtx: startCtx,
        startedAtPerf: this.mrStartedAtPerf,
        mediaRequestedAtPerf: this.mediaRequestedAtPerf,
        inputLatencySec: (this.ctx as any)?.baseLatency ?? 0,
        peak: peakOf(data),
        clipped: peakOf(data) >= 0.999,
        deviceId: this.trackSettings().deviceId,
        label: 'Take 1',
      },
    ];
  }

  private teardownCapture() {
    this.clearCaptureGraph();
    this.mediaRecorder = null;
    this.blocks = [];
    this.mrChunks = [];
  }

  /** Mantém o microfone aberto para teste de nível sem gravar. */
  async startMetering(ctx: AudioContext): Promise<void> {
    this.ctx = ctx;
    if (!this.stream) await this.requestPermission(undefined, ctx);
    if (!this.sourceNode) {
      this.sourceNode = ctx.createMediaStreamSource(this.stream!);
      this.captureGain = ctx.createGain();
      this.captureGain.gain.value = this.inputGain;
      this.sourceNode.connect(this.captureGain);
      this.analyser = ctx.createAnalyser();
      this.analyser.fftSize = 1024;
      this.analyser.smoothingTimeConstant = 0;
      this.captureGain.connect(this.analyser);
      this.captureSink = ctx.createGain();
      this.captureSink.gain.value = 0;
      this.sourceNode.connect(this.captureSink);
      this.captureSink.connect(ctx.destination);
    }
    this.startMeterLoop();
    this.setState('ready');
  }

  stopMetering() {
    this.stopMeterLoop();
    if (this.status === 'ready') this.setState('idle');
  }

  /** Libera o microfone (privacidade: nada fica aberto sem necessidade). */
  release() {
    this.stopMeterLoop();
    this.teardownCapture();
    this.releaseStream();
    this.setState('idle');
  }

  get hasPermission(): boolean {
    return !!this.stream && this.stream.getAudioTracks().some((t) => t.readyState === 'live');
  }
}

function concatMonoBuffers(parts: Float32Array[]): Float32Array {
  return concatMono(parts.map((p) => ({ length: p.length, getChannelData: () => p } as unknown as AudioBuffer)));
}

function downmix(buffer: AudioBuffer): Float32Array {
  const n = buffer.length;
  const out = new Float32Array(n);
  const ch = buffer.numberOfChannels;
  for (let c = 0; c < ch; c++) {
    const d = buffer.getChannelData(c);
    for (let i = 0; i < n; i++) out[i] += d[i] / ch;
  }
  return out;
}

function peakOf(data: Float32Array): number {
  let p = 0;
  for (let i = 0; i < data.length; i++) {
    const a = Math.abs(data[i]);
    if (a > p) p = a;
  }
  return p;
}

export function pickRecorderMime(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4',
    'audio/aac',
  ];
  for (const c of candidates) {
    try {
      if (MediaRecorder.isTypeSupported?.(c)) return c;
    } catch {
      /* noop */
    }
  }
  return undefined;
}

export function describeMicError(err: unknown): string {
  const e = err as { name?: string; message?: string };
  switch (e?.name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'Permissão de microfone negada. Toque no cadeado/ícone de permissão do navegador, permita o microfone e tente de novo.';
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'Nenhum microfone foi encontrado. Conecte um microfone ou fone com microfone e tente novamente.';
    case 'NotReadableError':
      return 'O microfone está em uso por outro aplicativo. Feche o outro app (chamada, gravador) e tente de novo.';
    case 'AbortError':
      return 'A solicitação do microfone foi cancelada. Tente novamente.';
    default:
      return e?.message
        ? `Não foi possível acessar o microfone: ${e.message}`
        : 'Não foi possível acessar o microfone.';
  }
}

export const recorderEngine = new RecorderEngine();

/** Classificação de nível usada no teste de microfone. */
export function classifyLevel(peak: number, rms: number): {
  label: 'MUITO BAIXO' | 'BAIXO' | 'BOM' | 'ALTO' | 'MUITO ALTO' | 'CLIPPING';
  advice: string;
  score: number;
} {
  const db = 20 * Math.log10(Math.max(1e-9, rms));
  const peakDb = 20 * Math.log10(Math.max(1e-9, peak));
  if (peak >= 0.995)
    return {
      label: 'CLIPPING',
      advice: 'Está distorcendo. Afaste um pouco o microfone ou diminua o ganho de entrada.',
      score: 0,
    };
  if (peakDb > -3)
    return {
      label: 'MUITO ALTO',
      advice: 'Quase distorcendo. Afaste um pouco o microfone.',
      score: 40,
    };
  if (db < -45)
    return {
      label: 'MUITO BAIXO',
      advice: 'Muito baixo. Aproxime o microfone (uns 15 cm) e cante no nível normal.',
      score: 15,
    };
  if (db < -33)
    return { label: 'BAIXO', advice: 'Ainda baixo. Aproxime um pouco o microfone.', score: 55 };
  if (db < -12 && peakDb < -4)
    return { label: 'BOM', advice: 'Nível ótimo para gravar.', score: 100 };
  return { label: 'ALTO', advice: 'Bom, mas com pouca folga. Cuidado nos refrões.', score: 78 };
}

export const clampLevel = clamp;
